import { from, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { Logging } from '../diagnostics/logging';
import { Rpc } from '../rpc/rpc';
import {
    ExtensionUpdateStatus,
    RpcSmeExtensionConfiguration,
    RpcSmeExtensionRequest,
    RpcSmeExtensionRequestResult,
    RpcSmeExtensionRequestType,
    RpcSmeExtensionResponseKey,
    RpcSmeExtensionUpdateStatus,
    SmeExtensionEntries,
    SmeExtensions,
    SmeExtensionSearchOptions
} from '../rpc/sme-extension/rpc-sme-extension-model';
import { RpcSmeExtensionRequestClient } from '../rpc/sme-extension/rpc-sme-extension-request-client';

/**
 * Sme Extension Module Side Service.
 */
export class SmeExtension {
    /**
     * Subject for tracking RPC responses from the extension host.
     */
    private watcher: Subject<RpcSmeExtensionRequestResult | RpcSmeExtensionUpdateStatus>;

    /**
     * The source name to use when logging about this service.
     */
    private get logSourceName() {
        return 'Extensions';
    }

    /**
     * Initializes a new instance of the Extension Manager class.
     *
     * @param rpc the rpc to forward auth requests to a parent window.
     */
    constructor(private rpc: Rpc) {
        this.watcher = new Subject<RpcSmeExtensionRequestResult | RpcSmeExtensionUpdateStatus>();
        this.rpc.register(RpcSmeExtensionResponseKey.command, this.onRpcResponse.bind(this));
    }

    /**
     * Get available extension request.
     *
     * @returns an observable for the response message from the extension host.
     */
    public getAvailableExtensions(): Observable<SmeExtensions> {
        const request: RpcSmeExtensionRequest = {
            requestId: MsftSme.newGuid(),
            requestType: RpcSmeExtensionRequestType.Available
        };
        return this.handleRpcRequest(request);
    }

    /**
     * Get installed extension request.
     *
     * @returns an observable for the response message from the extension host.
     */
    public getInstalledExtensions(): Observable<SmeExtensions> {
        const request: RpcSmeExtensionRequest = {
            requestId: MsftSme.newGuid(),
            requestType: RpcSmeExtensionRequestType.Installed
        };
        return this.handleRpcRequest(request);
    }

    /**
     * Get all extensions (available + installed) request.
     *
     * @returns an observable for the response message from the extension host.
     */
    public getAllExtensions(): Observable<SmeExtensions> {
        const request: RpcSmeExtensionRequest = {
            requestId: MsftSme.newGuid(),
            requestType: RpcSmeExtensionRequestType.All
        };
        return this.handleRpcRequest(request);
    }

    /**
     * Install the extension.
     *
     * @param config the configuration.
     * @returns an observable for installed result of extension.
     */
    public installExtension(config: RpcSmeExtensionConfiguration): Observable<SmeExtensions> {
        const request: RpcSmeExtensionRequest = {
            requestId: MsftSme.newGuid(),
            requestType: RpcSmeExtensionRequestType.InstallExtension,
            extensionConfiguration: config
        };

        return this.handleRpcExtensionWriteRequest(request);
    }

    /**
     * Gets the update status of extensions.
     *
     * @returns An observable for update status of extension availability.
     */
    public getUpdateStatus(): Observable<ExtensionUpdateStatus> {
        const request: RpcSmeExtensionRequest = {
            requestId: MsftSme.newGuid(),
            requestType: RpcSmeExtensionRequestType.UpdateStatus
        };

        return this.handleRpcUpdateStatusRequest(request);
    }

    /**
     * Search the entry points that meet the search options.
     *
     * @param searchOptions the search options
     */
    public searchEntriesByCondition(searchOptions: SmeExtensionSearchOptions): Observable<SmeExtensionEntries> {
        const request: RpcSmeExtensionRequest = {
            requestId: MsftSme.newGuid(),
            requestType: RpcSmeExtensionRequestType.SearchEntryPoints,
            extensionSearchOptions: searchOptions
        };

        return this.handleRpcSearchEntryPointsRequest(request);
    }

    /**
     * Internal method handles rpc request.
     *
     * @param request the result object
     * @returns an observable for installed and available extensions.
     */
    private handleRpcRequest(request: RpcSmeExtensionRequest): Observable<SmeExtensions> {
        Logging.logDebug(this.logSourceName, 'Incoming RPC request: {0}'.format(JSON.stringify(request)));
        return from(RpcSmeExtensionRequestClient.getExtensionsRequest(this.rpc, request))
            .pipe(
                mergeMap(() => this.watcher),
                filter((result) => result.requestId === request.requestId),
                take(1),
                map((result) => {
                    if (result) {
                        const extensionsResult = <RpcSmeExtensionRequestResult>result;
                        if (extensionsResult.error) {
                            throw result.error;
                        }

                        return extensionsResult.extensions;
                    }

                    throw new Error('Unexpected Response from Extension RPC Request');
                }));
    }

    /**
     * Handles the installation request of extension.
     *
     * @param request the request object.
     * @returns an observable for installed result.
     */
    private handleRpcExtensionWriteRequest(request: RpcSmeExtensionRequest): Observable<SmeExtensions> {
        Logging.logDebug(this.logSourceName, 'Incoming RPC request: {0}'.format(JSON.stringify(request)));
        return from(RpcSmeExtensionRequestClient.installExtension(this.rpc, request))
            .pipe(
                mergeMap(() => this.watcher),
                filter((result) => result.requestId === request.requestId),
                take(1),
                map((result) => {
                    if (result) {
                        const extensionsResult = <RpcSmeExtensionRequestResult>result;
                        if (extensionsResult.error) {
                            throw extensionsResult.error;
                        }

                        return extensionsResult.extensions;
                    }

                    throw new Error('Unexpected Response from Extension RPC Request');
                }));
    }

    /**
     * Handles rpc request of extension update status.
     *
     * @param request the result object
     * @returns an observable for update status of extension availability.
     */
    private handleRpcUpdateStatusRequest(request: RpcSmeExtensionRequest): Observable<ExtensionUpdateStatus> {
        Logging.logDebug(this.logSourceName, 'Incoming RPC request: {0}'.format(JSON.stringify(request)));
        return from(RpcSmeExtensionRequestClient.getExtensionsRequest(this.rpc, request))
            .pipe(
                mergeMap(() => this.watcher),
                filter(result => result.requestId === request.requestId),
                take(1),
                map(result => {
                    if (result) {
                        const statusResult = <RpcSmeExtensionUpdateStatus>result;
                        if (statusResult.error) {
                            throw statusResult.error;
                        }

                        return statusResult.status;
                    }

                    throw new Error('Unexpected Response from Extension RPC Request');
                }));
    }

    /**
     * Handles rpc request of extension search entry points.
     *
     * @param request the result object
     * @returns an observable for extension entries.
     */
    private handleRpcSearchEntryPointsRequest(request: RpcSmeExtensionRequest): Observable<SmeExtensionEntries> {
        Logging.logDebug(this.logSourceName, 'Incoming RPC request: {0}'.format(JSON.stringify(request)));
        return from(RpcSmeExtensionRequestClient.getExtensionsRequest(this.rpc, request))
            .pipe(
                mergeMap(() => this.watcher),
                filter(result => result.requestId === request.requestId),
                take(1),
                map(result => {
                    if (!result || !(<RpcSmeExtensionRequestResult>result).findResult) {
                        throw new Error('Unexpected Response from Extension RPC Request');
                    }

                    const response = <RpcSmeExtensionRequestResult>result;
                    if (response.error) {
                        throw new Error(response.error);
                    }

                    const entries: SmeExtensionEntries = <SmeExtensionEntries>{};
                    const findResult = response.findResult;
                    entries.conditionValidated = request.extensionSearchOptions.condition.validationRequired;
                    entries.installed = !!findResult.installed;
                    entries.upgradable = !!findResult.upgradable;
                    entries.entryPoints = [];
                    entries.entryPointsUpgradeable = undefined;
                    if (findResult.found) {
                        entries.entryPoints = findResult.entryPoints;
                        if (findResult.upgradable && findResult.entryPointsUpgradeable) {
                            entries.entryPointsUpgradeable = findResult.entryPointsUpgradeable;
                        }
                    }

                    return entries;
                }));
    }

    /**
     * Handles rpc response messages from the extension host.
     *
     * @param data the result of the extension host request.
     * @returns the promise of RPC response.
     */
    private onRpcResponse(data: RpcSmeExtensionRequestResult): Promise<any> {
        this.watcher.next(data);
        return Promise.resolve();
    }
}
